import { Container, Flex, Heading, SimpleGrid, Spacer, Stack, Text } from '@chakra-ui/react'

import { pluralize } from '@/utils/helpers'

import WebsiteSettingsQ from '@/queries/WebsiteSettingsQ'
import { useQuery } from '@/client/context/query'
import { CONTEST_END, CONTEST_START, type LeaderboardItem } from '@/client/services/keyClub'

import PublicImage from '@/components/PublicImage/PublicImage'
import ShwLink from '@/components/ShwLink'
import Tabs from '@/components/Tabs/Tabs'
import useUrlTabController from '@/components/Tabs/useUrlTabController'

const PROGRAM_INCENTIVES_URL =
  'https://docs.google.com/document/d/1EtU8WaMmG1mVMWRFOWJ_1Qre3GRMkiIcev_Eqa91p0M/edit?usp=sharing'

type LeaderboardTab = 'District' | 'Club' | 'Individual'
function LeaderboardPanel({
  items,
  unit,
  title,
}: {
  items: LeaderboardItem[]
  unit: string
  title: string
}): JSX.Element {
  return (
    <Stack
      direction="column"
      spacing="24px"
      border="2px solid"
      borderColor="border"
      borderRadius="8px"
      py="24px"
      px="16px"
      bg="bg"
    >
      <Text as="h3" fontSize="20px" fontWeight={500}>
        {title}
      </Text>
      <Stack direction="column" spacing="16px">
        {items.length === 0 ? (
          <Flex direction="column" justify="center" align="center">
            <PublicImage width="96px" height="96px" src="/leaderboard/empty-state.svg" my={4} />
            <Text>No one&apos;s here yet!</Text>
          </Flex>
        ) : (
          items.map(({ label, value }, index) => (
            <Stack direction="row" align="center" key={label} spacing="12px">
              <Text fontWeight="bold" fontSize="14px" color="fg.subtle">
                {index + 1}
              </Text>
              <Text fontWeight="bold">{label}</Text>
              <Spacer />
              <Text color="fg.muted" display={{ base: 'none', sm: 'flex' }}>
                {pluralize(value, unit)}
              </Text>
              <Text color="fg.muted" display={{ base: 'flex', sm: 'none' }}>
                {value}
              </Text>
            </Stack>
          ))
        )}
      </Stack>
    </Stack>
  )
}

export default function KeyClubTutorathonLeaderboard(): JSX.Element | null {
  const query = useQuery()

  const {
    districtParticipantsLeaderboard = [],
    districtPointsLeaderboard = [],
    clubParticipantsLeaderboard = [],
    clubPointsLeaderboard = [],
    userPointsLeaderboard = [],
  } = query(WebsiteSettingsQ.getKeyClubTutorathonLeaderboards()) ?? {}

  const [tab, tabToUrl] = useUrlTabController<LeaderboardTab>({
    queryKey: 'level',
    defaultTab: 'District',
    tabToQueryParam: {
      District: 'district',
      Club: 'club',
      Individual: 'individual',
    },
  })

  const tabToPointsLeaderboard: Record<LeaderboardTab, LeaderboardItem[]> = {
    District: districtPointsLeaderboard,
    Club: clubPointsLeaderboard,
    Individual: userPointsLeaderboard,
  }

  const tabToParticipantsLeaderboard: Record<LeaderboardTab, LeaderboardItem[]> = {
    District: districtParticipantsLeaderboard,
    Club: clubParticipantsLeaderboard,
    Individual: [],
  }

  return (
    <Container>
      <Stack direction="column" spacing="32px" align="center">
        <Heading size="2xl">🎉 The Schoolhouse Tutor-A-Thon 🎉</Heading>
        <Stack direction="column" spacing="16px">
          <Text>
            Want to make an impact and have fun while doing it? Looking for a service project that
            you can complete from the comfort of your bedroom? Announcing the{' '}
            <b>Schoolhouse Tutor-a-Thon</b>! Compete with fellow districts to join Schoolhouse and
            earn points by tutoring or learning on the platform.
          </Text>
          <Text>
            Each hour of tutoring earns 10 points, and each hour of learning earns 5 points – the
            winning district will be recognized and receive special rewards, so be sure to
            participate! Individuals and clubs will also continue to earn awards according to these{' '}
            <ShwLink href={PROGRAM_INCENTIVES_URL}>Program Incentives</ShwLink>.
          </Text>
          <Text>
            The contest will run from{' '}
            <b>
              {CONTEST_START.toFormat('MMMM dd, yyyy')} to {CONTEST_END.toFormat('MMMM dd, yyyy')}
            </b>
            . This is a great opportunity to boost your own studying for finals season and also
            bringing free education to learners around the world!
          </Text>
        </Stack>
        <Flex justify="flex-end">
          <Tabs<LeaderboardTab>
            variant="rounded"
            currentTab={tab}
            tabToUrl={tabToUrl}
            tabs={['District', 'Club', 'Individual']}
          />
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="16px" width="100%">
          <LeaderboardPanel items={tabToPointsLeaderboard[tab]} unit="point" title="Most Points" />
          <LeaderboardPanel
            items={tabToParticipantsLeaderboard[tab]}
            unit="participant"
            title="Most Participants"
          />
        </SimpleGrid>
      </Stack>
    </Container>
  )
}
